import { CheckCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { ReportStatusChip } from '@/features/report-list';
import type { ReportListStatus } from '@/models/report';
import type { TimelineEvent } from '../../types/historyEvent';
import { formatTimestamp } from '../../utils/formatters';
import styles from './TimelineItem.module.css';

const EVENT_CONFIG = {
  DRAFT_SAVED: {
    label: 'Draft Saved',
    actorPrefix: 'Edited by',
  },
  SIGNED: {
    label: 'Signed (Final)',
    actorPrefix: 'Signed by',
  },
  ADDENDUM_ADDED: {
    label: 'Addendum Added',
    actorPrefix: 'Added by',
  },
} as const;

interface TimelineItemProps {
  event: TimelineEvent;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

export default function TimelineItem({
  event,
  isSelected,
  onSelect,
}: TimelineItemProps) {
  const config = EVENT_CONFIG[event.kind];

  return (
    <button
      type="button"
      className={`${styles.item} ${isSelected ? styles.selected : ''}`}
      onClick={() => onSelect(event.id)}
      aria-pressed={isSelected}
    >
      <div className={styles.nodeCol}>
        <TimelineNode kind={event.kind} isSelected={isSelected} />
      </div>

      <div className={styles.content}>
        <div className={styles.titleRow}>
          <span className={styles.label}>{config.label}</span>
          <StatusChips tags={event.statusTags} />
          {event.isCurrent && (
            <span className={styles.currentBadge}>CURRENT</span>
          )}
        </div>

        <span className={styles.timestamp}>
          {formatTimestamp(event.timestamp)}
        </span>

        <span className={styles.actor}>
          {config.actorPrefix} {event.actorName}
        </span>
      </div>
    </button>
  );
}

function TimelineNode({
  kind,
  isSelected,
}: {
  kind: TimelineEvent['kind'];
  isSelected: boolean;
}) {
  if (kind === 'SIGNED') {
    return (
      <span className={`${styles.node} ${styles.nodeSigned}`}>
        <CheckCircleOutlined />
      </span>
    );
  }

  if (kind === 'ADDENDUM_ADDED') {
    return (
      <span className={`${styles.node} ${styles.nodeAddendum}`}>
        <PlusCircleOutlined />
      </span>
    );
  }

  return (
    <span
      className={`${styles.node} ${styles.nodeDraft} ${isSelected ? styles.nodeDraftSelected : ''}`}
    />
  );
}

function StatusChips({ tags }: { tags: string[] }) {
  if (!tags.length) return null;
  return (
    <>
      {tags.map((tag) => (
        <ReportStatusChip key={tag} status={tag as ReportListStatus} />
      ))}
    </>
  );
}
